import { atom } from 'jotai';
import type { ErrorEvent } from '@localagent/sdk';
import { configureAtomDevtools } from '../utils/tracking';
import { notificationMutePrefsAtom } from '../notifications/mute-prefs';
import type { ErrorEntry, ErrorSeverity } from '../../types/error-events-types';

export const errorEntriesAtom = configureAtomDevtools(
  atom<ErrorEntry[]>([]),
  'errors/entries'
);

// Mute is applied on READ, never by dropping frames on the way in. Prefs load
// asynchronously, so a write-time filter loses every frame that arrives before
// they land — and unmute would need a refetch to bring rows back. Deriving keeps
// mute non-lossy and retroactive (events ADR-006), and keeps the badge on the
// same predicate as the Errors tab list
// (events ADR-010).
export const visibleErrorEntriesAtom = configureAtomDevtools(
  atom<ErrorEntry[]>((get) => {
    const muted = get(notificationMutePrefsAtom).mutedOrigins;
    if (muted.length === 0) return get(errorEntriesAtom);
    return get(errorEntriesAtom).filter(e => !muted.includes(e.service_origin));
  }),
  'errors/visibleEntries'
);

export const unreadErrorCountAtom = configureAtomDevtools(
  atom<number>((get) => {
    const entries = get(visibleErrorEntriesAtom);
    return entries.filter(e => !e.read).length;
  }),
  'errors/unreadCount'
);

export const addErrorEntryAction = configureAtomDevtools(
  atom<null, [ErrorEntry], void>(null, (get, set, entry: ErrorEntry) => {
    const entries = get(errorEntriesAtom);

    const dedupeKey = `${entry.error_code}:${entry.service_origin}`;
    const recentCutoff = Date.now() - 30000;
    const existing = entries.find(
      e => `${e.error_code}:${e.service_origin}` === dedupeKey
        && new Date(e.timestamp).getTime() > recentCutoff
    );

    if (existing) {
      set(
        errorEntriesAtom,
        entries.map(e =>
          e.id === existing.id
            ? {
                ...e,
                occurrenceCount: e.occurrenceCount + 1,
                timestamp: entry.timestamp,
                eventIds: [...e.eventIds, ...entry.eventIds],
                read: e.read && entry.read,
              }
            : e
        )
      );
    } else {
      set(errorEntriesAtom, [entry, ...entries].slice(0, 100));
    }
  }),
  'errors/addEntry'
);

/**
 * Replace the errors store with an authoritative snapshot from the backend
 * `errors.list` response (drives the Errors-tab "Retry" refresh). One
 * row per event (no client-side occurrence folding) so the refreshed panel
 * reflects the durable list verbatim — including per-org read state, `details`,
 * and `session_id`. Live push frames continue to prepend + fold on top.
 */
export const setErrorEntriesFromListAction = configureAtomDevtools(
  atom<null, [ErrorEvent[]], void>(null, (_get, set, errors: ErrorEvent[]) => {
    const mapped: ErrorEntry[] = errors.map((e) => ({
      id: `error-list-${e.event_id}`,
      eventIds: [e.event_id],
      error_code: e.error_code,
      message: e.message,
      service_origin: e.service_origin,
      severity: e.severity as ErrorSeverity,
      workspace_id: e.workspace_id,
      session_id: e.session_id,
      details: e.details,
      timestamp: e.timestamp,
      occurrenceCount: 1,
      read: e.read === true,
    }));
    set(errorEntriesAtom, mapped.slice(0, 100));
  }),
  'errors/setFromList'
);

// "All" means all the user can SEE: a muted row keeps its unread state so
// unmuting is still retroactive
// (events ADR-010).
export const markAllReadAction = configureAtomDevtools(
  atom<null, [], void>(null, (get, set) => {
    const visible = new Set(get(visibleErrorEntriesAtom).map(e => e.id));
    set(
      errorEntriesAtom,
      get(errorEntriesAtom).map(e => (visible.has(e.id) ? { ...e, read: true } : e))
    );
  }),
  'errors/markAllRead'
);

export const toggleErrorReadAction = configureAtomDevtools(
  atom<null, [string], void>(null, (get, set, id: string) => {
    const entries = get(errorEntriesAtom);
    set(
      errorEntriesAtom,
      entries.map(e => (e.id === id ? { ...e, read: !e.read } : e))
    );
  }),
  'errors/toggleRead'
);

export const clearErrorsAction = configureAtomDevtools(
  atom<null, [], void>(null, (get, set) => {
    const visible = new Set(get(visibleErrorEntriesAtom).map(e => e.id));
    set(errorEntriesAtom, get(errorEntriesAtom).filter(e => !visible.has(e.id)));
  }),
  'errors/clear'
);
